import { Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { UserStatusChip } from '@components/Chip';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';

import { formatDate, formatName } from '@utils/format';

import type { GetAccountUsersQuery } from 'generated/account.generated';
import type { RoleModel, UserModel, UserStatus } from 'generated/types';

const messages = defineMessages({
  name: {
    id: 'organization.setting.usersTable.name',
    defaultMessage: 'Name',
  },
  email: {
    id: 'organization.setting.usersTable.email',
    defaultMessage: 'Email Address',
  },
  roles: {
    id: 'organization.setting.usersTable.roles',
    defaultMessage: 'Roles',
  },
  latestActivity: {
    id: 'organization.setting.usersTable.latestActivity',
    defaultMessage: 'Latest Activity',
  },
  status: {
    id: 'organization.setting.usersTable.status',
    defaultMessage: 'Status',
  },
  noUsers: {
    id: 'organization.setting.usersTable.noUsers',
    defaultMessage: 'No Users',
  },
});

type RowData = {
  user_id: string;
  name?: string | null;
  email?: string | null;
  roles: RoleModel[];
  last_login?: string | null;
  status: UserStatus;
  user: Omit<
    UserModel,
    'account' | 'disabled' | 'open_alert_count' | 'roles' | 'users'
  >;
};

interface UserManagementTableProps {
  data?: GetAccountUsersQuery['getAccountRecord']['users'];
  title?: React.ReactNode;
}

export default function UserManagementTable({
  data,
  title = null,
}: UserManagementTableProps) {
  const { formatMessage } = useIntl();

  const tableData = (data ?? []).map<RowData>((user) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { account, roles, vitall_patient, ...rest } = user;
    const { email, first_name, last_login, last_name, status, user_id } = rest;

    return {
      user_id,
      name: formatName(first_name, last_name),
      email,
      roles,
      last_login,
      status,
      user: rest,
    };
  });

  return (
    <MuiDataTable
      columns={[
        {
          name: 'name',
          className: 'small',
          label: formatMessage(messages.name),
          options: {
            sort: true,
          },
        },
        {
          name: 'email',
          label: formatMessage(messages.email),
          options: {
            sort: true,
            customBodyRender: (email?: string | null) => {
              return (
                <Typography sx={{ wordBreak: 'break-word' }} variant="body2">
                  {email}
                </Typography>
              );
            },
          },
        },
        {
          name: 'roles',
          label: formatMessage(messages.roles),
          options: {
            sort: true,
            customBodyRender: (roles: RoleModel[]) => {
              return (
                <Typography variant="caption">
                  {roles.map(({ role_name }) => role_name).join(', ')}
                </Typography>
              );
            },
          },
        },
        {
          name: 'last_login',
          className: 'date',
          label: formatMessage(messages.latestActivity),
          options: {
            sort: true,
            customBodyRender: (value?: string | null) =>
              value ? formatDate({ date: value, formatter: 'datetime' }) : '-',
          },
        },
        {
          name: 'status',
          className: 'small',
          label: formatMessage(messages.status),
          options: {
            filter: true,
            sort: true,
            customBodyRender: (value: RowData['status']) => {
              return <UserStatusChip status={value} />;
            },
          },
        },
        /*
        {
          name: 'user',
          options: {
            customHeadLabelRender: () => null,
            customBodyRender: () => {
              return (
                <IconButton
                  color="text.secondary"
                  disabled
                  iconType="fas"
                  name="pencil"
                  onClick={() => {}}
                />
              );
            },
          },
        },
        */
      ]}
      data={tableData}
      options={{
        search: true,
        sort: true,
        filter: true,
        // sortOrder: {},
        textLabels: {
          body: {
            noMatch: formatMessage(messages.noUsers),
          },
        },
      }}
      title={title}
    />
  );
}
